/**
 * Channel management and my sidebar on TanStack Query: optimistic writes on the persisted workspace cache (create,
 * settings, leave, star/mute/notify, drag-and-drop moves, my folders), queued while offline and replayed in order
 * with their clientId as the Idempotency-Key. They share the message outbox scope, so a channel created offline
 * exists before the messages queued for it are sent. Pending writes are re-applied on top of every bootstrap refetch
 * and every sidebar event, so neither a background refresh nor an echo hides a queued edit. A write the server
 * refuses is rolled back by refetching the workspace.
 */
import type {
  Channel,
  ChannelMembership,
  CreateChannelRequest,
  CreateSidebarFolderRequest,
  MoveSidebarItemsResponse,
  PersistentEvent,
  SidebarFolder,
  SidebarMove,
  UpdateChannelRequest,
  UpdateMembershipRequest,
  UpdateSidebarFolderRequest,
} from "@bundle/contracts";
import { Channels, errorKind, Sidebar, Workspace, type ApiClient, type WorkspaceCache } from "@bundle/core";
import { MutationObserver, type Mutation, type QueryClient } from "@tanstack/query-core";
import { ulidIds } from "../clock.ts";
import { keys, updateWorkspace } from "./keys.ts";

/** The message outbox scope (mutations.ts SEND_SCOPE; scopes match by id). */
const SEND_SCOPE = { id: "outbox" };

type Base = { ws: string; clientId: string };
export type CreateChannelVars = Base & { kind: "createChannel"; meId: string; at: number; req: CreateChannelRequest & { id: string } };
export type UpdateChannelVars = Base & { kind: "updateChannel"; channelId: string; req: UpdateChannelRequest };
export type LeaveChannelVars = Base & { kind: "leaveChannel"; channelId: string };
export type MembershipVars = Base & { kind: "membership"; channelId: string; req: UpdateMembershipRequest };
export type MovesVars = Base & { kind: "moves"; moves: SidebarMove[] };
export type CreateFolderVars = Base & { kind: "createFolder"; req: CreateSidebarFolderRequest & { id: string } };
export type UpdateFolderVars = Base & { kind: "updateFolder"; folderId: string; req: UpdateSidebarFolderRequest };
export type DeleteFolderVars = Base & { kind: "deleteFolder"; folderId: string };
export type ChannelVars =
  | CreateChannelVars
  | UpdateChannelVars
  | LeaveChannelVars
  | MembershipVars
  | MovesVars
  | CreateFolderVars
  | UpdateFolderVars
  | DeleteFolderVars;

const PREFIX = "channels.";

/** The optimistic effect of one write (also re-applied on top of refetched data while still pending). */
function applyWrite(c: WorkspaceCache, v: ChannelVars): WorkspaceCache {
  switch (v.kind) {
    case "createChannel":
      if (c.channels[v.req.id] !== undefined) return c;
      return Channels.addChannel(c, Channels.optimisticChannel(v.req, v.req.id, v.meId, c.workspace.id, v.at), v.at);
    case "updateChannel":
      return Channels.applyChannelUpdate(c, v.channelId, v.req);
    case "leaveChannel":
      return Channels.applyLeave(c, v.channelId);
    case "membership":
      return Sidebar.applyMembershipPatch(c, v.channelId, v.req);
    case "moves":
      return Sidebar.applySidebarMoves(c, v.moves);
    case "createFolder": {
      if (c.sidebarFolders?.[v.req.id] !== undefined) return c;
      const f: SidebarFolder = { id: v.req.id, name: v.req.name.trim(), rank: v.req.rank ?? Sidebar.nextFolderRank(c), collapsed: false };
      if (v.req.emoji !== undefined && v.req.emoji !== "") f.emoji = v.req.emoji;
      if (v.req.icon !== undefined && v.req.icon !== "") f.icon = v.req.icon;
      return Workspace.upsertSidebarFolder(c, f);
    }
    case "updateFolder":
      return Sidebar.patchSidebarFolder(c, v.folderId, v.req);
    case "deleteFolder":
      return Workspace.removeSidebarFolder(c, v.folderId);
  }
}

function isChannelMutation(m: Mutation<unknown, Error, unknown, unknown>): boolean {
  const key = m.options.mutationKey;
  return Array.isArray(key) && typeof key[0] === "string" && key[0].startsWith(PREFIX);
}

/** Writes not yet confirmed (in flight, waiting to retry, or paused offline), oldest first. */
function pendingWrites(client: QueryClient, ws: string): ChannelVars[] {
  return client
    .getMutationCache()
    .getAll()
    .filter((m) => isChannelMutation(m) && (m.state.status === "pending" || m.state.isPaused))
    .map((m) => m.state.variables as ChannelVars | undefined)
    .filter((v): v is ChannelVars => v !== undefined && v.ws === ws);
}

/** `cache` with this device's pending channel / sidebar writes re-applied (bootstrap refetches, events). */
export function withPendingChannels(client: QueryClient, ws: string, cache: WorkspaceCache, except?: ChannelVars): WorkspaceCache {
  let c = cache;
  for (const v of pendingWrites(client, ws)) if (v !== except) c = applyWrite(c, v);
  return c;
}

/** After a realtime event: keep queued sidebar / channel edits on top of what the server said. */
export function reapplyAfterEvent(client: QueryClient, ws: string, ev: PersistentEvent): void {
  if (
    ev.type !== "membership.updated" &&
    ev.type !== "sidebar_item.updated" &&
    ev.type !== "sidebar_folder.updated" &&
    ev.type !== "sidebar_folder.deleted" &&
    ev.type !== "channel.updated" &&
    ev.type !== "channel.member_left"
  ) {
    return;
  }
  if (pendingWrites(client, ws).length === 0) return;
  updateWorkspace(client, ws, (c) => withPendingChannels(client, ws, c));
}

/** Runs a write (optimistic at once); resolves with the server's answer, rejects when refused for good. */
type NewVars<V> = V extends unknown ? Omit<V, "clientId"> & { clientId?: string } : never;

export function mutateChannel<T>(client: QueryClient, vars: NewVars<ChannelVars>): Promise<T> {
  const full = { ...vars, clientId: vars.clientId ?? ulidIds.next() } as ChannelVars;
  return new MutationObserver<unknown, unknown, ChannelVars>(client, { mutationKey: [`${PREFIX}${full.kind}`], scope: SEND_SCOPE }).mutate(full) as Promise<T>;
}

function setChannel(c: WorkspaceCache, channel: Channel): WorkspaceCache {
  return { ...c, channels: { ...c.channels, [channel.id]: channel } };
}

export function registerChannelMutations(client: QueryClient, api: ApiClient): void {
  const settle = (v: ChannelVars, f: (c: WorkspaceCache) => WorkspaceCache) =>
    updateWorkspace(client, v.ws, (c) => withPendingChannels(client, v.ws, f(c), v));
  const rollback = (e: unknown, v: ChannelVars) => {
    if (errorKind(e) === "network") return;
    if (v.kind === "createChannel") updateWorkspace(client, v.ws, (c) => Channels.removeChannel(c, v.req.id));
    void client.invalidateQueries({ queryKey: keys.workspace(v.ws) });
  };
  const defaults = <V extends ChannelVars, R>(kind: V["kind"], fn: (v: V) => Promise<R>, done: (c: WorkspaceCache, r: R, v: V) => WorkspaceCache) => {
    client.setMutationDefaults([`${PREFIX}${kind}`], {
      scope: SEND_SCOPE,
      mutationFn: (v: V) => fn(v),
      onMutate: (v: V) => updateWorkspace(client, v.ws, (c) => applyWrite(c, v)),
      onSuccess: (r: R, v: V) => settle(v, (c) => done(c, r, v)),
      onError: rollback,
    });
  };
  defaults<CreateChannelVars, Channel>("createChannel", (v) => api.createChannel(v.ws, v.req), (c, ch) => setChannel(c, ch));
  defaults<UpdateChannelVars, Channel>(
    "updateChannel",
    (v) => api.updateChannel(v.ws, v.channelId, v.req, v.clientId),
    (c, ch) => (c.memberships[ch.id] !== undefined || (ch.kind === "public" && !ch.archived) ? setChannel(c, ch) : Channels.removeChannel(c, ch.id)),
  );
  defaults<LeaveChannelVars, void>("leaveChannel", (v) => api.leaveChannel(v.ws, v.channelId, v.clientId), (c) => c);
  defaults<MembershipVars, ChannelMembership>("membership", (v) => api.updateMembership(v.ws, v.channelId, v.req, v.clientId), (c, ms) => Workspace.setMembership(c, ms));
  defaults<MovesVars, MoveSidebarItemsResponse>("moves", (v) => api.moveSidebarItems(v.ws, v.moves, v.clientId), (c, r) => {
    let next = c;
    for (const ms of r.memberships) next = Workspace.setMembership(next, ms);
    for (const it of r.items) next = Workspace.setSidebarItem(next, it);
    return next;
  });
  defaults<CreateFolderVars, SidebarFolder>("createFolder", (v) => api.createSidebarFolder(v.ws, v.req, v.clientId), (c, f) => Workspace.upsertSidebarFolder(c, f));
  defaults<UpdateFolderVars, SidebarFolder>("updateFolder", (v) => api.updateSidebarFolder(v.ws, v.folderId, v.req, v.clientId), (c, f) => Workspace.upsertSidebarFolder(c, f));
  defaults<DeleteFolderVars, void>("deleteFolder", (v) => api.deleteSidebarFolder(v.ws, v.folderId, v.clientId), (c) => c);
}
